import React, { useEffect, useMemo, useRef, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { durationLabel, sampleSeriesData } from "../../utils/monitoringUtils";
import { downloadPathRunCsv, downloadPathRunJson } from "../../utils/monitoringDownloads";

const COLORS = { live: "#ea580c", baseline: "#64748b", future: "#2563eb", futureBaseline: "#94a3b8" };

function addSeries(chart, field, name, color, dashed = false) {
  const series = chart.series.push(new am4charts.LineSeries());
  series.dataFields.dateX = "date";
  series.dataFields.valueY = field;
  series.name = name;
  series.stroke = am4core.color(color);
  series.strokeWidth = 2.5;
  if (dashed) series.strokeDasharray = "5,4";
  series.tooltipText = `{name}: [bold]{valueY.formatNumber('#.##')}[/]`;
  series.connect = true;
  const bullet = series.bullets.push(new am4charts.CircleBullet());
  bullet.circle.radius = 3;
  return series;
}

function TimeSeriesChart({ data }) {
  const chartRef = useRef(null);
  useEffect(() => {
    if (!chartRef.current || !data.length) return undefined;
    const chart = am4core.create(chartRef.current, am4charts.XYChart);
    chart.data = data;
    chart.padding(8, 12, 0, 0);
    const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.renderer.minGridDistance = 55;
    dateAxis.tooltipDateFormat = "MMM d, h:mm a";
    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.title.text = "Minutes";
    valueAxis.min = 0;
    addSeries(chart, "liveMinutes", "Live duration", COLORS.live);
    addSeries(chart, "liveBaselineMinutes", "Live baseline", COLORS.baseline, true);
    addSeries(chart, "futureMinutes", "One-week duration", COLORS.future);
    addSeries(chart, "futureBaselineMinutes", "One-week baseline", COLORS.futureBaseline, true);
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.xAxis = dateAxis;
    chart.legend = new am4charts.Legend();
    chart.legend.position = "bottom";
    chartRef.current.chart = chart;
    return () => chart.dispose();
  }, [data]);
  return <div className="dc-chart" ref={chartRef} />;
}

export default function PathRunCharts({ collectionId, runId, path, samples, isLoading, error }) {
  const firstSegments = samples.find((sample) => sample.segments?.length)?.segments || [];
  const [viewKey, setViewKey] = useState("whole");
  const chartData = useMemo(() => sampleSeriesData(samples, viewKey), [samples, viewKey]);
  const latest = samples[samples.length - 1]?.whole_path;

  if (isLoading) return <div className="dc-chart-state">Loading samples…</div>;
  if (error) return <div className="dc-error">{error}</div>;
  if (!samples.length) return <div className="dc-chart-state">No samples have been written for this path yet.</div>;

  return (
    <div className="dc-run-path-data">
      <div className="dc-run-toolbar">
        <label>Chart data<select value={viewKey} onChange={(event) => setViewKey(event.target.value)}>
          <option value="whole">Whole path</option>
          {firstSegments.map((segment, index) => <option key={index} value={index}>Point {segment.from_point_index + 1} → Point {segment.to_point_index + 1}</option>)}
        </select></label>
        <div>
          <button type="button" onClick={() => downloadPathRunCsv(collectionId, runId, path, samples)}>Download CSV</button>
          <button type="button" onClick={() => downloadPathRunJson(collectionId, runId, path, samples)}>Download JSON</button>
        </div>
      </div>
      {latest && <div className="dc-latest-metrics">
        <span><small>Latest live duration</small><strong>{durationLabel(latest.live_duration_seconds)}</strong></span>
        <span><small>Static baseline</small><strong>{durationLabel(latest.live_baseline_duration_seconds)}</strong></span>
        <span><small>Versus baseline</small><strong className={(latest.live_traffic_delay_percent || 0) > 20 ? "hot" : ""}>{Number(latest.live_traffic_delay_percent || 0).toFixed(1)}%</strong></span>
      </div>}
      <section className="dc-chart-card"><h4>Travel time comparison</h4><TimeSeriesChart data={chartData} /></section>
    </div>
  );
}
